import { Box, Text, TextLink, Skeleton, Stack, Badge, Notice } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { useTranslation, Trans } from 'react-i18next'
import { useEffect } from 'react'
import { useFormikContext, FormikContextType } from 'formik'
import useGetPromotions from '@/hooks/use-promotions'
import { toast } from '@/components/toast-notifications'
import { useGetUsages } from '@/hooks/use-block-and-pay'
import { useWizardContext } from '@/components/modal-wizard-form/wizard-context'
import { Product, PLinkData } from '@/pages/UsageCredits/types'
import getSyndicationCount from '@/pages/UsageCredits/utils/get-syndication-credits-count'
import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import { DialogEventHandler } from '@/types'
import CheckboxWithOptions from './checkbox-with-options'
import { SYNDICATION_PLINK } from './content'

type Props = {
  closeModal: DialogEventHandler
  creditCount?: number
  setHasEnoughCredits: (a: boolean) => void
}

const SelectProductsStep = ({ closeModal, creditCount = 0, setHasEnoughCredits }: Props) => {
  const { t } = useTranslation()
  const { isLoading: isLoadingUsages } = useGetUsages()
  const { values, errors }: FormikContextType<{ platform: string[] }> = useFormikContext()

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const { setIsLoading, setLoadingFinished } = useWizardContext()
  const planText = useGetPlanKey()
  const state = 'BC'
  const {
    data: productData,
    isLoading,
    error
  }: { data: PLinkData; isLoading: boolean; error: any } = useGetPromotions({
    country: 'CA',
    province: state ?? 'BC',
    promotionId: SYNDICATION_PLINK,
    addon: true
  })
  useEffect(() => {
    if (isLoading) {
      setIsLoading()
    } else {
      setLoadingFinished()
    }
  }, [isLoading, setIsLoading, setLoadingFinished])

  const products = productData?.products?.[0]?.items
  let facebookProducts = [] as Product[]
  let craigslistProducts = [] as Product[]
  let zumperProducts = [] as Product[]
  products?.forEach((product: Product) => {
    if (product?.metadata?.platform === 'facebook') {
      facebookProducts.push(product)
    } else if (product?.metadata?.platform === 'craigslist') {
      craigslistProducts.push(product)
    } else if (product?.metadata?.platform === 'zumper') {
      zumperProducts.push(product)
    }
  })

  if (error) {
    toast(
      <Trans i18nKey="l.usage_credits.purchase.error.text">
        Sorry! An unexpected error has occured. Please try again later or contact
        <TextLink href="mailto:support@liv.rent">support@liv.rent.</TextLink>
      </Trans>,
      {
        type: 'error'
      }
    )
    closeModal()
  }

  const syndicationCount = getSyndicationCount(values, products)
  useEffect(() => {
    if (syndicationCount > creditCount) {
      setHasEnoughCredits(false)
    } else {
      setHasEnoughCredits(true)
    }
  }, [syndicationCount, creditCount, setHasEnoughCredits])
  return (
    <Box paddingY="gutter" style={{ width: isMobile ? '100%' : '660px' }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="spaceBetween"
        width="full"
        paddingBottom="gutter"
      >
        <Box display="flex" alignItems="center" justifyContent="spaceBetween">
          <Text size="medium" weight="medium">
            {t('l.usage.credits.select_platforms.text', 'Select platforms')}
          </Text>
          <Box component="span" style={{ color: vars.foregroundColor.critical }}>
            *
          </Box>
        </Box>
        {isLoadingUsages ? (
          <Skeleton.Shape height={58} width={'100%'} />
        ) : (
          <Badge tone="info">
            {`${t(
              'l.usage.credits.available_credits_count.text',
              'Available credits:'
            )}  ${creditCount}`}
          </Badge>
        )}
      </Box>
      {isLoading ? (
        <Skeleton.Shape height={58} width={'100%'} />
      ) : (
        <Stack space="small">
          <Stack space="small" dividers>
            <CheckboxWithOptions
              id="facebook"
              name="platform"
              trackingId={`usage-credit-${planText}-checkout-dialog-multiplatform-adv-fb`}
              label={t('b.common.facebook.text', 'Facebook')}
              products={facebookProducts}
              dropDownLabel={t('l.usage.credits.duration.text', 'Duration')}
            />
            <Box display="flex" alignItems="center" flexDirection="row">
              <CheckboxWithOptions
                id="craigslist"
                name="platform"
                trackingId={`usage-credit-${planText}-checkout-dialog-multiplatform-adv-craigslist`}
                products={craigslistProducts}
                label={t('b.common.craigslist.text', 'Craigslist')}
                dropDownLabel={t('l.usage.credits.duration.text', 'Duration')}
                disabled={true}
              />
              <Box paddingBottom="xsmall" marginLeft="xsmall">
                <Badge tone="neutral">
                  {t('l.usage.credits.currently_unavailable.text', 'Currently unavailable')}
                </Badge>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" flexDirection="row">
              <CheckboxWithOptions
                id="zumper"
                name="platform"
                trackingId={`usage-credit-${planText}-checkout-dialog-multiplatform-adv-zumper`}
                products={zumperProducts}
                label={t('b.common.zumper.text', 'Zumper')}
                dropDownLabel={t('l.usage.credits.duration.text', 'Duration')}
                disabled={true}
              />
              <Box paddingBottom="xsmall" marginLeft="xsmall">
                <Badge tone="neutral">
                  {t('l.usage.credits.currently_unavailable.text', 'Currently unavailable')}
                </Badge>
              </Box>
            </Box>
          </Stack>

          {errors?.platform ? (
            <Notice tone="critical">
              <Text>{errors.platform}</Text>
            </Notice>
          ) : null}
          {values?.platform?.length > 0 && (
            <SyndicationNotice syndicationCount={syndicationCount} />
          )}
        </Stack>
      )}
    </Box>
  )
}

const SyndicationNotice = ({ syndicationCount }: { syndicationCount: number }) => {
  const { t } = useTranslation()
  return (
    <Notice tone="info">
      <Text>
        <Trans
          i18nKey="l.usage_credits.syndication.syndication_credits_count.text"
          syndicationCount={syndicationCount}
          t={t}
        >
          {{ syndicationCount }} syndication credits will be automatically used on this listing.
        </Trans>
      </Text>
    </Notice>
  )
}

export default SelectProductsStep
